import { memo, useCallback, useMemo, useRef } from "react"

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Tooltip,
} from "@coval/ui"
import * as SliderPrimitive from "@radix-ui/react-slider"

import { formatSeekTime } from "@/lib/audio-player-utils"

import {
  formatSimulationOffset,
  type SimulationDataFrameMark,
} from "../utils/simulationDataFrames"

const FRAME_BAR_HEIGHT = 12

interface AudioFrameTracksProps {
  marks: SimulationDataFrameMark[]
  tables: string[]
  colors: string[]
  durationMs: number
  currentTime: number
  selectedId?: string
  onSelect: (mark: SimulationDataFrameMark, seconds?: number) => void
  onSeek: (seconds: number) => void
  minHeight?: number
  label?: string
  showTimeAxis?: boolean
  inset: { leftPx: number; rightPx: number; tailPx?: number }
}

function framePosition(mark: SimulationDataFrameMark, durationMs: number) {
  const start = Math.max(0, Math.min(100, (mark.startMs / durationMs) * 100))
  const end = Math.max(start, Math.min(100, (mark.endMs / durationMs) * 100))
  return { left: `${start}%`, width: `${end - start}%`, minWidth: 3 }
}

function groupPointFrames(marks: SimulationDataFrameMark[]) {
  const groups: SimulationDataFrameMark[][] = []
  const points = new Map<number, SimulationDataFrameMark[]>()
  for (const mark of marks) {
    if (mark.startMs !== mark.endMs) {
      groups.push([mark])
      continue
    }
    const position = mark.startMs
    const group = points.get(position)
    if (group) {
      group.push(mark)
    } else {
      const newGroup = [mark]
      points.set(position, newGroup)
      groups.push(newGroup)
    }
  }
  return groups
}

function FrameMarker({
  marks,
  durationMs,
  color,
  selectedId,
  onSelect,
  onSeek,
  clickTimeAt,
}: Pick<
  AudioFrameTracksProps,
  "marks" | "durationMs" | "selectedId" | "onSelect" | "onSeek"
> & {
  color: string
  clickTimeAt: (clientX: number) => number | undefined
}) {
  const clickedTime = useRef<number | undefined>(undefined)
  const mark = marks[0]
  const offset = formatSimulationOffset(mark.startMs)
  const hasCoincidentFrames = marks.length > 1
  const label = hasCoincidentFrames
    ? `${mark.tableName} ${marks.length} frames at ${offset}`
    : `${mark.tableName} frame at ${offset}`
  const isSelected = marks.some((item) => item.id === selectedId)
  const button = (
    <Button
      type="button"
      variant="ghost"
      aria-label={label}
      aria-pressed={isSelected}
      onPointerDown={(event) => {
        clickedTime.current = clickTimeAt(event.clientX)
      }}
      onKeyDown={() => {
        clickedTime.current = undefined
      }}
      onClick={(event) => {
        const seconds =
          event.detail > 0 ? clickTimeAt(event.clientX) : undefined
        if (hasCoincidentFrames) {
          if (seconds !== undefined) {
            onSeek(seconds)
          }
        } else {
          onSelect(mark, seconds)
        }
      }}
      className={`pointer-events-auto absolute top-1/2 h-3 -translate-y-1/2 overflow-hidden rounded-sm border px-0 text-xs transition-opacity focus-visible:z-10 ${isSelected ? "opacity-100" : "opacity-60 hover:opacity-80"}`}
      style={{
        ...framePosition(mark, durationMs),
        height: FRAME_BAR_HEIGHT,
        borderColor: color,
        backgroundColor: color,
      }}
    />
  )
  if (!hasCoincidentFrames) {
    return button
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>
      <DropdownMenuContent
        className="ph-mask max-h-56 overflow-y-auto"
        data-dd-privacy="mask"
        data-ph-capture="false"
      >
        <DropdownMenuLabel>
          {marks.length} frames at {offset}
        </DropdownMenuLabel>
        {marks.map((item, index) => (
          <DropdownMenuItem
            key={item.id}
            onSelect={() => onSelect(item, clickedTime.current)}
          >
            Frame {index + 1}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const AudioFrameOverlay = memo(function AudioFrameOverlay({
  marks,
  tables,
  colors,
  durationMs,
  selectedId,
}: Omit<
  AudioFrameTracksProps,
  "inset" | "onSelect" | "onSeek" | "currentTime"
>) {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      {marks.map((mark) => (
        <div
          key={mark.id}
          className="absolute inset-y-0 border-x"
          style={{
            ...framePosition(mark, durationMs),
            borderColor: colors[tables.indexOf(mark.tableName)],
            backgroundColor: colors[tables.indexOf(mark.tableName)],
            opacity: mark.id === selectedId ? 0.3 : 0.08,
          }}
        />
      ))}
    </div>
  )
})

export function AudioFrameTracks({
  marks,
  tables,
  colors,
  durationMs,
  currentTime,
  selectedId,
  onSelect,
  onSeek,
  minHeight = 0,
  inset,
  label = "Data Frame tracks",
  showTimeAxis = true,
}: AudioFrameTracksProps) {
  const seekTrack = useRef<HTMLSpanElement>(null)
  const clickTimeAt = useCallback(
    (clientX: number) => {
      const rect = seekTrack.current?.getBoundingClientRect()
      if (!rect || rect.width <= 0 || !Number.isFinite(clientX)) {
        return undefined
      }
      const fraction = Math.max(
        0,
        Math.min(1, (clientX - rect.left) / rect.width),
      )
      return (fraction * durationMs) / 1000
    },
    [durationMs],
  )
  const trackStyle = {
    marginLeft: inset.leftPx,
    marginRight: inset.rightPx - (inset.tailPx ?? 0),
  }
  const lanes = useMemo(() => {
    const trackStyle = {
      marginLeft: inset.leftPx,
      marginRight: inset.rightPx - (inset.tailPx ?? 0),
    }
    return tables.map((table, index) => (
      <div key={table} className="pointer-events-none relative h-8">
        <Tooltip
          content={table}
          className="ph-mask"
          contentDataAttributes={{
            "data-dd-privacy": "mask",
            "data-ph-capture": "false",
          }}
        >
          <span
            role="button"
            tabIndex={0}
            className="pointer-events-auto absolute left-4 top-3 -translate-y-1/2 cursor-default select-none truncate font-mono text-xs text-coval-text-muted"
            style={{ maxWidth: inset.leftPx - 24 }}
          >
            {table}
          </span>
        </Tooltip>
        <div
          className="relative h-6 overflow-hidden rounded bg-surface-1"
          style={trackStyle}
        >
          {groupPointFrames(
            marks.filter((mark) => mark.tableName === table),
          ).map((group) => (
            <FrameMarker
              key={group[0].id}
              marks={group}
              durationMs={durationMs}
              color={colors[index]}
              selectedId={selectedId}
              onSelect={onSelect}
              onSeek={onSeek}
              clickTimeAt={clickTimeAt}
            />
          ))}
        </div>
      </div>
    ))
  }, [
    marks,
    tables,
    colors,
    durationMs,
    selectedId,
    onSelect,
    onSeek,
    clickTimeAt,
    inset.leftPx,
    inset.rightPx,
    inset.tailPx,
  ])
  return (
    <div
      className={showTimeAxis ? "space-y-1 py-3" : "space-y-1 py-1"}
      aria-label={label}
    >
      {showTimeAxis && (
        <div
          className="relative h-5 text-xs tabular-nums text-coval-text-muted"
          style={trackStyle}
        >
          <span className="absolute left-0">0s</span>
          <span className="absolute left-1/2 -translate-x-1/2">
            {formatSimulationOffset(durationMs / 2)}
          </span>
          <span className="absolute right-0">
            {formatSimulationOffset(durationMs)}
          </span>
        </div>
      )}
      <div data-audio-timeline-viewport className="max-h-56 overflow-y-auto">
        <div className="relative" style={{ minHeight }}>
          <SliderPrimitive.Root
            ref={seekTrack}
            data-audio-timeline-duration={durationMs / 1000}
            min={0}
            max={durationMs}
            step={1}
            value={[Math.max(0, Math.min(durationMs, currentTime * 1000))]}
            onValueChange={([milliseconds]) => onSeek(milliseconds / 1000)}
            className="absolute inset-y-0 touch-none select-none cursor-pointer focus-within:ring-1 focus-within:ring-coval-focus-ring"
            style={{
              left: inset.leftPx,
              right: inset.rightPx - (inset.tailPx ?? 0),
            }}
          >
            <SliderPrimitive.Thumb
              aria-label={`${label} seek position`}
              aria-valuetext={formatSeekTime(currentTime)}
              className="coval-custom-focus-ring block size-0"
            />
          </SliderPrimitive.Root>
          {lanes}
        </div>
      </div>
    </div>
  )
}
